import React, { useCallback } from "react";
import { createNewAnnotation } from "api/factories/annotations.factory";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Point, Rectangle } from "react-shape-editor";
import { Annotation } from "store/features/annotations/interfaces";
import { getUserId } from "store/features/authorization";
import { useAnnotationsHandler, useMaterialSpecs } from ".";
import { STICKER_SIZE } from "../components/constants";
import { AnnotationFrame, AnnotationFrameResizeContainer } from "../components";

const constrainMove = ({ x, y, width, height, vectorHeight, vectorWidth }: ReactShapeMoveConstrainProps) => {
    return {
        x: Math.round(Math.min(vectorWidth - width, Math.max(0, x))),
        y: Math.round(Math.min(vectorHeight - height, Math.max(0, y))),
    };
};

const constrainResize = ({
    movingCorner: { x: movingX, y: movingY },
    vectorHeight,
    vectorWidth,
}: ReactShapeResizeConstrainProps) => {
    return {
        x: Math.round(Math.min(vectorWidth, Math.max(0, movingX))),
        y: Math.round(Math.min(vectorHeight, Math.max(0, movingY))),
    };
};

interface TimeStamp {
    timeStampSeconds?: number;
}

interface ReactShapeMoveConstrainProps {
    x: number;
    y: number;
    width: number;
    height: number;
    originalX: number;
    originalY: number;
    vectorWidth: number;
    vectorHeight: number;
}

interface ReactShapeResizeConstrainProps {
    originalMovingCorner: Point;
    startCorner: Point;
    movingCorner: Point;
    lockedDimension: "x" | "y" | null;
    vectorWidth: number;
    vectorHeight: number;
}

export const useAnnotationPainter = () => {
    const { setTab, assetId } = useMaterialSpecs();
    const { t } = useTranslation();
    const getCreatorLabel = useCallback(() => t("campaign.annotations.label.me"), [t]);
    const userId = useSelector(getUserId);
    const {
        annotations,
        isDrawingMode,
        annotationDrawingType,
        onAddAnnotation,
        onUpdateAnnotation,
        selectedAnnotation,
        onAnnotationSelect,
        showAnnotations,
        showReviewed,
    } = useAnnotationsHandler();

    const drawHandler = useCallback(
        ({ x, y, timeStampSeconds }: Point & TimeStamp) => {
            if (isDrawingMode && annotationDrawingType === "STICKER") {
                onAddAnnotation(
                    createNewAnnotation(
                        "STICKER",
                        getCreatorLabel(),
                        {
                            x: Math.floor(x),
                            y: Math.floor(y),
                            width: STICKER_SIZE,
                            height: STICKER_SIZE,
                        },
                        userId,
                        assetId,
                        timeStampSeconds
                    )
                );
            }
        },
        [annotationDrawingType, isDrawingMode, onAddAnnotation, getCreatorLabel, userId, assetId]
    );

    const addShapeHandler = useCallback(
        ({ x, y, width, height, timeStampSeconds }: Rectangle & TimeStamp) => {
            if (isDrawingMode && annotationDrawingType === "FRAME") {
                onAddAnnotation(
                    createNewAnnotation(
                        "FRAME",
                        getCreatorLabel(),
                        {
                            x: Math.floor(x),
                            y: Math.floor(y),
                            width: Math.floor(width),
                            height: Math.floor(height),
                        },
                        userId,
                        assetId,
                        timeStampSeconds
                    )
                );
            }
        },
        [annotationDrawingType, isDrawingMode, onAddAnnotation, getCreatorLabel, userId, assetId]
    );

    const shapes = annotations
        .filter((annotation) => showReviewed || !annotation.reviewed)
        .map((annotation) => {
            const { height, width, x, y } = annotation.position;

            const isSelected = (annotation: Annotation) =>
                selectedAnnotation && selectedAnnotation.selectionId === annotation.selectionId;

            return showAnnotations ? (
                <AnnotationFrame
                    key={annotation.selectionId}
                    shapeId={`${annotation.selectionId}`}
                    height={height}
                    width={width}
                    x={x}
                    y={y}
                    active={isSelected(annotation)}
                    onFocus={() => {
                        onAnnotationSelect(annotation);
                        setTab("ANNOTATIONS");
                    }}
                    onChange={(frame) => {
                        onUpdateAnnotation({
                            ...annotation,
                            modified: true,
                            position: { ...frame },
                        });
                    }}
                    constrainMove={constrainMove}
                    constrainResize={constrainResize}
                    extraShapeProps={{
                        color: annotation.color,
                        type: annotation.type,
                        isSelected: isSelected(annotation),
                        timeStampSeconds: annotation.timeStampSeconds,
                    }}
                    ResizeHandleComponent={(params) => {
                        if (annotation.type === "STICKER") {
                            return null;
                        }

                        return <AnnotationFrameResizeContainer {...params} />;
                    }}
                />
            ) : null;
        });

    return {
        drawHandler,
        shapes,
        addShapeHandler,
    };
};
